/**
 * Onboarding profile completion route — collects essential user information.
 *
 * New users arrive here after their first OTP login. The form collects:
 *
 * - **Full name** (required) — display name used throughout the platform.
 * - **Birthday** (required) — used for AI chat context with students.
 *
 * On successful submission the user is navigated back to `/onboard` where
 * the hub component re-evaluates their state and shows the track chooser.
 *
 * If the user already has a name set (e.g. navigated here by mistake),
 * they are redirected back to `/onboard` immediately.
 *
 * @module routes/onboard-profile
 */

import { safeReturnTo } from '@learnai/api/auth-guard';
import { useAuth } from '@learnai/api/auth-provider';
import { tryCatch } from '@learnai/tools/try-catch';
import { DatePicker } from '@learnai/ui/components/blocks/date-picker';
import { Button } from '@learnai/ui/components/ui/button';
import { Card, CardContent } from '@learnai/ui/components/ui/card';
import { Input } from '@learnai/ui/components/ui/input';
import { Label } from '@learnai/ui/components/ui/label';
import { toast } from '@learnai/ui/components/ui/sonner';
import { Spinner } from '@learnai/ui/components/ui/spinner';
import { api } from '@web/lib/api';
import { UserIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Minimum selectable birthday (1900-01-01). */
const BIRTHDAY_FROM = new Date(1900, 0, 1);

/** Maximum selectable birthday (today). */
function getBirthdayTo(): Date {
	const t = Temporal.Now.plainDateISO();
	return new Date(t.year, t.month - 1, t.day);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OnboardProfile() {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { user, isLoading: authLoading, profileComplete, refreshUser } = useAuth();

	const returnTo = safeReturnTo(searchParams.get('returnTo')) ?? '/onboard';

	const [name, setName] = useState('');
	const [birthday, setBirthday] = useState('');
	const [loading, setLoading] = useState(false);

	// -------------------------------------------------------------------
	// Pre-populate from authenticated user; redirect away if already done
	// -------------------------------------------------------------------

	useEffect(() => {
		if (authLoading || !user) return;

		// If profile is already complete, honour returnTo (so completing
		// this form via a no-op visit also routes correctly).
		if (profileComplete) {
			if (returnTo.startsWith('/') && !returnTo.startsWith('//') && returnTo !== window.location.pathname) {
				// Cross-zone targets (e.g. /admin, /learn) require a full nav.
				if (returnTo.startsWith('/admin') || returnTo.startsWith('/learn')) {
					window.location.href = returnTo;
				} else {
					navigate(returnTo, { replace: true });
				}
			} else {
				navigate('/onboard', { replace: true });
			}
			return;
		}

		// Pre-populate any fields that already have values.
		if (user.name) setName(user.name);
		if (typeof user.birthday === 'string' && user.birthday.length > 0) setBirthday(user.birthday);
	}, [authLoading, user, profileComplete, returnTo, navigate]);

	// -------------------------------------------------------------------
	// Handlers
	// -------------------------------------------------------------------

	async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement, SubmitEvent>) {
		e.preventDefault();
		e.stopPropagation();
		setLoading(true);

		const trimmedName = name.trim();
		if (trimmedName.length === 0) {
			toast.error('Please enter your full name.');
			setLoading(false);
			return;
		}

		if (!birthday) {
			toast.error('Please enter your date of birth.');
			setLoading(false);
			return;
		}

		const { error: updateErr } = await tryCatch(
			api.users.updateProfile({
				name: trimmedName,
				birthday,
			}),
		);

		if (updateErr) {
			toast.error(updateErr instanceof Error ? updateErr.message : 'Failed to save profile. Please try again.');
			setLoading(false);
			return;
		}

		// Refresh the auth provider so profileComplete reflects the new
		// values before we navigate.
		await refreshUser();

		// Honour returnTo if it points outside the shell, navigate via SPA otherwise.
		if (returnTo.startsWith('/admin') || returnTo.startsWith('/learn')) {
			window.location.href = returnTo;
			return;
		}
		navigate(returnTo, { replace: true });
	}

	// -------------------------------------------------------------------
	// Guards
	// -------------------------------------------------------------------

	if (authLoading || (user && profileComplete)) {
		return (
			<div className="flex h-screen w-full items-center justify-center">
				<Spinner className="size-6 text-muted-foreground" />
			</div>
		);
	}

	// -------------------------------------------------------------------
	// Render
	// -------------------------------------------------------------------

	return (
		<div className="flex min-h-screen w-full items-center justify-center px-4">
			<Card className="w-full max-w-md overflow-hidden">
				<CardContent className="p-6 md:p-8">
					<form onSubmit={handleSubmit}>
						<div className="flex flex-col gap-6">
							{/* ── Header ──────────────────────────────── */}
							<div className="flex flex-col items-center gap-3 text-center">
								<div className="flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
									<UserIcon className="size-6" />
								</div>
								<div>
									<h1 className="text-2xl font-bold">Welcome to LearnAI</h1>
									<p className="mt-1 text-balance text-muted-foreground">
										Tell us a bit about yourself to get started.
									</p>
								</div>
							</div>

							{/* ── Full name ───────────────────────────── */}
							<div className="grid gap-2">
								<Label htmlFor="name">
									Full Name <span className="text-destructive">*</span>
								</Label>
								<Input
									id="name"
									name="name"
									type="text"
									placeholder="Jane Doe"
									value={name}
									onChange={(e) => setName(e.target.value)}
									required
									autoFocus
									autoComplete="name"
									maxLength={100}
								/>
							</div>

							{/* ── Birthday ────────────────────────────── */}
							<div className="grid gap-2">
								<DatePicker
									id="birthday"
									label={
										<>
											Date of Birth <span className="text-destructive">*</span>
										</>
									}
									placeholder="Select date"
									value={birthday}
									onChange={setBirthday}
									fromDate={BIRTHDAY_FROM}
									toDate={getBirthdayTo()}
								/>
								<p className="text-xs text-muted-foreground">Used to personalise your learning experience.</p>
							</div>

							{/* ── Submit ───────────────────────────────── */}
							<Button type="submit" className="w-full" disabled={loading || !name.trim() || !birthday}>
								{loading ? <Spinner className="size-3" /> : 'Continue'}
							</Button>

							{/* ── Signed-in hint ──────────────────────── */}
							{user && (
								<p className="text-center text-xs text-muted-foreground">
									Signed in as <span className="font-medium text-foreground">{user.email}</span>
								</p>
							)}
						</div>
					</form>
				</CardContent>
			</Card>
		</div>
	);
}
